import * as Popover from "@radix-ui/react-popover";
import { Command as CommandPrimitive } from "cmdk";
import * as React from "react";

import { faChevronDown } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleX } from "@performyard/fontawesome/pro-solid-svg-icons";

import { SelectOptionItem } from "../components/SelectOptionItem";
import { SelectSearchHeader } from "../components/SelectSearchHeader";

import { Option } from "../../../../types/Option";

import { useSelectBase } from "../../../../hooks";

import { cn } from "../../../../lib/utils";
import { getOptionKey } from "../utils/optionKey";

import {
	CLEAR_BUTTON_BASE_STYLES,
	DROPDOWN_ANIMATIONS,
	DROPDOWN_VARIANTS,
	SCROLL_STYLES,
} from "../../../../lib/styles";

import { ButtonIcon } from "../../../atoms/ButtonIcon";
import { Icon } from "../../../atoms/Icon";
import type { InputSize } from "../../../atoms/InputBase";
import { inputContainerVariants, useInputFlash } from "../../../atoms/InputBase";
import { InputBase } from "../../../atoms/InputBase";
import { useInputField } from "../../InputWrapper/InputContext";

const getCursorStyles = (isDisabled: boolean) => ({
	container: isDisabled ? "cursor-not-allowed" : "cursor-pointer",
	input: cn("caret-transparent truncate", isDisabled ? "cursor-not-allowed" : "cursor-pointer"),
});

type SingleSelectInputProps<T> = {
	/**
	 * Size variant for the select input.
	 * @default "medium"
	 */
	size?: InputSize;
	/**
	 * Array of options to display in the dropdown. If not provided and `fetchOptions` is used, options will be fetched dynamically.
	 */
	options?: Option<T>[];
	/**
	 * Async function to fetch options based on search term. When provided, enables server-side search and filtering.
	 * @param searchTerm - The current search term entered by the user
	 * @returns Promise that resolves to an array of options matching the search term
	 */
	fetchOptions?: (searchTerm: string) => Promise<Option<T>[]>;
	/**
	 * Currently selected value. Can be `null` or `undefined` for no selection. Controlled component - parent manages state.
	 */
	value?: T;
	/**
	 * Callback function called when selection changes.
	 * @param value - The new selected value, or `null` if cleared
	 */
	onChange: (value: T | null) => void;
	/**
	 * Optional callback function called when search value changes (combobox variant only, when search input is visible).
	 * @param searchValue - The current search term
	 */
	onSearchChange?: (searchValue: string) => void;
	/**
	 * Whether the input is in an error state (displays red border).
	 * @default false
	 */
	errored?: boolean;
	/**
	 * Whether the input is disabled (not interactive, grayed out).
	 * @default false
	 */
	disabled?: boolean;
	/**
	 * Selection variant mode.
	 * - "select": Simple dropdown without search (default)
	 * - "combobox": Dropdown with search input and server-side filtering support
	 * Both variants use virtualization and support keyboard navigation.
	 * @default "select"
	 */
	selectionVariant?: "select" | "combobox";
	/**
	 * Placeholder text shown when no option is selected.
	 * @default "Select an option..."
	 */
	placeholder?: string;
	/**
	 * Unique identifier for the input element. If not provided, will use context ID from InputWrapper or generate one.
	 */
	id?: string;
	/**
	 * Test identifier for the input element (used for testing).
	 */
	"data-testid"?: string;
	/**
	 * Whether to show a clear button when a value is selected.
	 * @default false
	 */
	isClearable?: boolean;
	/**
	 * Custom render function for displaying the selected value in the input.
	 * @param value - The selected value to render
	 * @param option - The full option object (if available)
	 * @returns React node to render as the selected value display
	 */
	renderValue?: (value: T, option?: Option<T>) => React.ReactNode;
	/**
	 * Custom render function for displaying options in the dropdown.
	 * @param option - The option to render
	 * @param isSelected - Whether this option is currently selected
	 * @returns React node to render for the option
	 */
	renderOption?: (option: Option<T>, isSelected: boolean) => React.ReactNode;
	/**
	 * Optional CSS classes to apply to the component container (use with caution).
	 */
	dangerouslySetClassName?: string;
	/**
	 * Configuration for virtualized rendering (applies to both variants).
	 */
	virtualizationConfig?: {
		/**
		 * Height of each virtualized item in pixels.
		 * @default 32
		 */
		itemHeight?: number;
		/**
		 * Maximum height of the dropdown container in pixels.
		 * @default 240
		 */
		maxHeight?: number;
	};
	/**
	 * Preferred position for the dropdown to appear relative to the trigger.
	 * Radix UI will automatically flip to the opposite side if there's not enough space.
	 * @default "bottom"
	 */
	position?: "top" | "right" | "bottom" | "left";

	/* the number of options that have to be present for the combobox styling */
	minOptionsForCombobox?: number;

	/**
	 * Accessible label for screen readers when no visible label is present.
	 */
	ariaLabel?: string;

	/** Change this number to play a one-time blue→white background flash (e.g. after an applied AI suggestion). */
	flashKey?: number;
};

const SingleSelectInput = <T,>({
	size = "medium",
	options: externalOptions = [],
	fetchOptions,
	value,
	onChange,
	onSearchChange,
	errored,
	disabled,
	selectionVariant = "select",
	placeholder = "Select an option...",
	id,
	"data-testid": dataTestId,
	isClearable = false,
	renderValue,
	renderOption,
	dangerouslySetClassName,
	virtualizationConfig,
	position = "bottom",
	minOptionsForCombobox,
	ariaLabel,
	flashKey,
}: SingleSelectInputProps<T>) => {
	const textSizeClassName = size === "small" ? "text-caption" : "text-paragraph-3";
	const { flashing, onAnimationEnd } = useInputFlash(flashKey);

	const { id: contextId } = useInputField();
	const hasAssociatedLabel = !!contextId;

	const result = useSelectBase<T>({
		mode: "single",
		options: externalOptions,
		fetchOptions,
		value,
		onChange,
		onSearchChange,
		errored,
		disabled,
		selectionVariant,
		virtualizationConfig,
		id,
	});

	if (result.mode !== "single") {
		throw new Error("Expected single mode");
	}

	const {
		inputId,
		hasError,
		isDisabled,
		isOpen,
		searchValue,
		isLoading,
		selectedOption,
		filteredOptions,
		options,
		triggerWidth,
		triggerRef,
		searchInputRef,
		parentRef,
		virtualizer,
		containerHeight,
		focusedIndex,
		isKeyboardNavActive,
		handleKeyDown,
		handleItemMouseEnter,
		handleItemMouseLeave,
		setSearchValue,
		handleOpenChange,
		handleItemSelect,
	} = result;

	const handleClear = () => {
		if (!isDisabled) {
			onChange(null);
			setSearchValue("");
			onSearchChange?.("");
		}
	};

	const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
		if (isDisabled) return;

		switch (e.key) {
			case "ArrowDown":
			case "ArrowUp":
				if (!isOpen) {
					e.preventDefault();
					handleOpenChange(true);
				} else {
					e.preventDefault();
					e.stopPropagation();
					if (parentRef.current) {
						parentRef.current.focus();
						handleKeyDown(e);
					}
				}
				break;
			case "Enter":
			case " ":
				e.preventDefault();
				if (!isOpen) {
					handleOpenChange(true);
				}
				break;
			case "Escape":
				if (isOpen) {
					e.preventDefault();
					e.stopPropagation();
					handleOpenChange(false);
				}
				break;
		}
	};

	const displayValue = selectedOption
		? typeof selectedOption.label === "string"
			? selectedOption.label
			: typeof selectedOption.label === "number"
				? String(selectedOption.label)
				: ""
		: "";

	const getSelectedValueDisplay = () => {
		if (value === null || value === undefined || !renderValue) return null;
		return renderValue(value, selectedOption);
	};
	let selectionVariantToUse = selectionVariant;
	if (minOptionsForCombobox) {
		if (options?.length <= minOptionsForCombobox) {
			selectionVariantToUse = "select";
		}
	}

	React.useEffect(() => {
		if (isOpen && selectedOption) {
			const selectedIndex = filteredOptions.findIndex((option) => option.value === selectedOption.value);
			if (selectedIndex !== -1) {
				const timeoutId = setTimeout(() => {
					const scrollElement = parentRef.current;
					if (scrollElement) {
						const itemHeight = virtualizationConfig?.itemHeight ?? 32;
						const viewportHeight = containerHeight;
						const selectedItemOffset = selectedIndex * itemHeight;
						const centerOffset = Math.max(0, selectedItemOffset - viewportHeight / 2 + itemHeight / 2);

						scrollElement.scrollTop = centerOffset;
					}
				}, 50);

				return () => clearTimeout(timeoutId);
			}
		}
	}, [isOpen, selectedOption, filteredOptions, containerHeight, parentRef, virtualizationConfig]);

	const cursorStyles = getCursorStyles(isDisabled);
	const iconSize = size === "small" ? 12 : 16;

	const renderTrigger = (triggerProps?: React.ComponentPropsWithoutRef<typeof Popover.Trigger>) => {
		const triggerContent =
			renderValue && selectedOption ? (
				<div
					className={cn(
						inputContainerVariants({
							size,
							errored: hasError,
							disabled: isDisabled,
							flash: flashing,
						})
					)}
					onAnimationEnd={onAnimationEnd}
				>
					<div
						className={cn(
							"line-clamp-1 flex max-w-[calc(100%-16px)] min-w-0 flex-1 items-center data-[is-clearable=true]:pr-8",
							textSizeClassName
						)}
					>
						{getSelectedValueDisplay()}
					</div>
					{isClearable && selectedOption && <div className="w-4" />}
					<Icon icon={faChevronDown} size={iconSize} color="neutral-5" />
				</div>
			) : (
				<InputBase
					size={size}
					value={displayValue}
					placeholder={placeholder}
					errored={hasError}
					disabled={isDisabled}
					flashKey={flashKey}
					id={inputId}
					data-testid={dataTestId}
					dangerouslySetInputClassName={cn(
						size === "small" && "!text-caption",
						cursorStyles.input,
						selectionVariantToUse === "combobox" ? "truncate" : ""
					)}
					dangerouslySetClassName={cursorStyles.container}
					rightActionElement={
						<div className="flex items-center gap-1">
							{isClearable && selectedOption && <div className="w-4" />}
							<Icon icon={faChevronDown} size={iconSize} color="neutral-5" />
						</div>
					}
				/>
			);

		return (
			<Popover.Trigger asChild {...triggerProps}>
				<div
					ref={triggerRef}
					className={cn(isDisabled && "cursor-not-allowed")}
					onKeyDown={handleTriggerKeyDown}
					role="button"
					tabIndex={isDisabled ? -1 : 0}
					aria-expanded={isOpen}
					aria-haspopup="listbox"
					aria-labelledby={ariaLabel || !hasAssociatedLabel ? undefined : `label-${inputId}`}
					aria-label={ariaLabel}
				>
					{triggerContent}
				</div>
			</Popover.Trigger>
		);
	};

	const renderClearButton = () => {
		if (!isClearable || !selectedOption) return null;

		return (
			<ButtonIcon
				icon={faCircleX}
				size={iconSize}
				color="neutral"
				shade={3}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					handleClear();
				}}
				disabled={isDisabled}
				aria-label="Clear selection"
				dangerouslySetClassName={CLEAR_BUTTON_BASE_STYLES}
			/>
		);
	};

	const renderDropdownContent = () => {
		return (
			<CommandPrimitive className="flex w-full flex-col overflow-hidden rounded-1" shouldFilter={false}>
				{selectionVariantToUse === "combobox" && (
					<SelectSearchHeader
						options={options}
						fetchOptions={fetchOptions}
						searchValue={searchValue}
						onSearchChange={(newSearchValue: string) => {
							setSearchValue(newSearchValue);
							onSearchChange?.(newSearchValue);
						}}
						searchInputRef={searchInputRef}
						onKeyDown={handleKeyDown}
						parentRef={parentRef}
						textSizeClassName={textSizeClassName}
					/>
				)}

				<div
					role="listbox"
					aria-label="Options list"
					className="max-h-60 overflow-x-hidden overflow-y-auto focus:outline-hidden"
					style={{
						...SCROLL_STYLES,
						height: `${containerHeight}px`,
						minHeight: `${containerHeight}px`,
					}}
					ref={parentRef}
					onKeyDown={handleKeyDown}
					tabIndex={0}
				>
					{isLoading ? (
						<div className={`px-3 py-2 ${textSizeClassName} text-neutral-5`}>Loading options...</div>
					) : filteredOptions.length === 0 ? (
						selectionVariantToUse === "combobox" ? (
							<CommandPrimitive.Empty className={`px-3 py-2 ${textSizeClassName} text-neutral-5`}>
								No options found
							</CommandPrimitive.Empty>
						) : (
							<div className={`px-3 py-2 ${textSizeClassName} text-neutral-5`}>No options available</div>
						)
					) : (
						<CommandPrimitive.Group>
							<div
								style={{
									height: `${virtualizer.getTotalSize()}px`,
									width: "100%",
									position: "relative",
								}}
							>
								{virtualizer
									.getVirtualItems()
									.map((virtualItem: { index: number; size: number; start: number }) => {
										const option = filteredOptions[virtualItem.index];
										if (!option) return null;

										const isSelected = selectedOption?.value === option.value;
										const isFocused = isKeyboardNavActive && focusedIndex === virtualItem.index;

										return (
											<SelectOptionItem
												key={getOptionKey(option)}
												option={option}
												isSelected={isSelected}
												isFocused={isFocused}
												onSelect={() => {
													if (!option.disabled && !isDisabled) {
														handleItemSelect(option);
													}
												}}
												renderOption={renderOption}
												virtualItem={virtualItem}
												onMouseEnter={() => handleItemMouseEnter(virtualItem.index)}
												onMouseLeave={handleItemMouseLeave}
												showCheckmark={true}
												textSizeClassName={textSizeClassName}
											/>
										);
									})}
							</div>
						</CommandPrimitive.Group>
					)}
				</div>
			</CommandPrimitive>
		);
	};

	return (
		<div data-is-clearable={isClearable} className={cn("relative w-full", dangerouslySetClassName)}>
			<Popover.Root open={isOpen} onOpenChange={handleOpenChange} modal={false}>
				{renderTrigger()}
				{renderClearButton()}

				<Popover.Portal container={document.body}>
					<Popover.Content
						className={cn(DROPDOWN_VARIANTS({ hasBorder: true }), DROPDOWN_ANIMATIONS)}
						side={position}
						align="start"
						sideOffset={8}
						style={{
							width: triggerWidth ? `${triggerWidth}px` : "var(--radix-popover-trigger-width)",
							minWidth: triggerWidth ? `${triggerWidth}px` : "var(--radix-popover-trigger-width)",
							zIndex: 9999,
						}}
						onOpenAutoFocus={(e: Event) => e.preventDefault()}
						onCloseAutoFocus={(e: Event) => e.preventDefault()}
					>
						{renderDropdownContent()}
					</Popover.Content>
				</Popover.Portal>
			</Popover.Root>
		</div>
	);
};

SingleSelectInput.displayName = "SingleSelectInput";

export { SingleSelectInput, type SingleSelectInputProps };
